import React, { useState, useEffect } from 'react';
import UnifiedHeader from '@/components/layout/UnifiedHeader';
import HomeLayout from '@/components/layout/HomeLayout';
import NavigationDots from '@/components/layout/NavigationDots';
// Language is provided globally in App.tsx; do not nest another provider here
// import { LanguageProvider } from '@/contexts/LanguageContext';
// Legacy hook replaced by unified effective content (home_blocks preferred)
import { useEffectiveHomeContent } from '@/hooks/useEffectiveHomeContent';
import type { HomeSection } from '@/hooks/useHomeSections';
import { useMediaLibrary } from '@/hooks/useSupabaseData';
import MetaTags from '@/components/layout/MetaTags';
import DynamicHomeSection from '@/components/home/DynamicHomeSection';
import SectionTransition from '@/components/transitions/SectionTransition';
import { useSectionBackup } from '@/hooks/useSectionBackup';

// Dynamic section renderer hook
const useDynamicSections = (): {
  sections: HomeSection[];
  isLoading: boolean;
  getSectionBackground: (sectionKey: string) => string | null;
} => {
  const { data: sections, isLoading } = useEffectiveHomeContent();
  const { media: heroBackgrounds } = useMediaLibrary('hero-bg');
  const { media: aboutBackgrounds } = useMediaLibrary('about-bg');
  const { media: visionBackgrounds } = useMediaLibrary('vision-bg');
  const { media: contactBackgrounds } = useMediaLibrary('contact-bg');

  const getSectionBackground = (sectionKey: string) => {
    switch (sectionKey) {
      case 'hero':
        return heroBackgrounds?.[0]?.file_url;
      case 'about':
        return aboutBackgrounds?.[0]?.file_url;
      case 'vision':
        return visionBackgrounds?.[0]?.file_url;
      case 'contact':
      case 'contact_cta':
      case 'contact-cta':
        return contactBackgrounds?.[0]?.file_url;
      default:
        return null;
    }
  };

  const dynamicSections: HomeSection[] = (sections?.filter((section) => section.is_active) || []) as HomeSection[];
  
  return {
    sections: dynamicSections,
    isLoading,
    getSectionBackground
  };
};

// Legacy sections removed from the fullpage flow to avoid duplication and theming conflicts.

type DotConfig = Array<{ name_ar: string; name_en: string; theme: 'light' | 'dark' }>;

const FullpageScroll = ({ children, onSectionChange, sectionConfig }: { 
  children: React.ReactNode; 
  onSectionChange?: (index: number) => void;
  sectionConfig?: DotConfig;
}) => {
  const [currentSection, setCurrentSection] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const sections = React.Children.toArray(children);
  const totalSections = sections.length;

  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollToSection = React.useCallback((index: number) => {
    if (index < 0 || index >= totalSections) return;
    if (isScrolling) return;
    setIsScrolling(true);
    setCurrentSection(index);
    onSectionChange?.(index); // Notify parent of section change

    const element = document.getElementById(`section-${index}`);
    if (element) {
      element.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    }
    window.setTimeout(() => setIsScrolling(false), 800);
  }, [totalSections, isScrolling, onSectionChange, prefersReducedMotion]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isScrolling) return;
      switch (e.key) {
        case 'ArrowDown':
        case 'PageDown':
          if (currentSection < totalSections - 1) {
            scrollToSection(currentSection + 1);
          }
          break;
        case 'ArrowUp':
        case 'PageUp':
          if (currentSection > 0) {
            scrollToSection(currentSection - 1);
          }
          break;
        case 'Home':
          scrollToSection(0);
          break;
        case 'End':
          scrollToSection(totalSections - 1);
          break;
      }
    };

    let touchStartY = 0;
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (isScrolling) return;
      const touchEndY = e.changedTouches[0].clientY;
      const deltaY = touchStartY - touchEndY;
      if (Math.abs(deltaY) > 50) {
        if (deltaY > 0 && currentSection < totalSections - 1) {
          scrollToSection(currentSection + 1);
        } else if (deltaY < 0 && currentSection > 0) {
          scrollToSection(currentSection - 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentSection, isScrolling, totalSections, scrollToSection]);

  // IntersectionObserver to track current section within the scroll container
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const pgSections = Array.from(root.querySelectorAll('.pg-section')) as HTMLElement[];
    if (!pgSections.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];
        if (!visible) return;
        const idxAttr = (visible.target as HTMLElement).getAttribute('data-section-index');
        const idx = idxAttr ? parseInt(idxAttr, 10) : NaN;
        if (!Number.isNaN(idx) && idx !== currentSection) {
          setCurrentSection(idx);
          onSectionChange?.(idx);
        }
      },
      { root, threshold: [0.5] }
    );

    pgSections.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [onSectionChange, currentSection]);

  return (
    <>
      <NavigationDots 
        activeSection={currentSection} 
	onSectionChange={scrollToSection} 
	sectionConfig={sectionConfig}
      />
      <div
        ref={containerRef}
	style={{ scrollSnapType: 'y mandatory', height: '100svh', overflowY: 'auto', overflowX: 'hidden' }}
      >
        {sections.map((section, index) => (
          <div
            key={index}
            id={`section-${index}`}
            style={{ scrollSnapAlign: 'start' }}
          >
            {section}
          </div>
        ))}
      </div>
    </>
  );
};

const IndexContent = () => {
  const [currentSection, setCurrentSection] = useState(0);
  const { sections, isLoading, getSectionBackground } = useDynamicSections();
  const { autoBackup } = useSectionBackup();

  const updateCurrentSection = (index: number) => {
    setCurrentSection(index);
  };

  // Build effective sections ensuring About + Sectors as second after Hero (PROTECTED)
  const effectiveSections = React.useMemo(() => {
    const list: HomeSection[] = sections || [];
    
    const heroIndex = list.findIndex((s) => s.section_key === 'hero');
    const aboutIndex = list.findIndex((s) => s.section_key === 'about' || s.section_key === 'about-sectors');
    const hero = heroIndex !== -1 ? list[heroIndex] : null;
    const hasAboutLike = aboutIndex !== -1;
    const others = list.filter((_s, idx) => idx !== heroIndex && idx !== aboutIndex);

    // PROTECTED synthetic about-sectors section (cannot be lost)
    const synthetic: HomeSection = {
      id: 'synthetic-about-protected',
      section_key: 'about-sectors',
      title_ar: 'من نحن',
      title_en: 'About Us',
      sort_order: 1,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // LOCK MECHANISM: Always ensure Hero is first, About-Sectors is second
    const base: HomeSection[] = (() => {
      const acc: HomeSection[] = [];
      if (hero) acc.push(hero);
      if (hasAboutLike) {
        acc.push(list[aboutIndex]!);
      } else {
        acc.push(synthetic);
      }
      acc.push(...others);
      return acc;
    })();

    // Ensure Section 03 — Investment Philosophy appears as third when missing (synthetic fallback)
    const hasInvestmentPhilosophy = base.some((s) => s.section_key === 'investment-philosophy' || s.section_key === 'philosophy');
    if (!hasInvestmentPhilosophy) {
      const now = new Date().toISOString();
      const syntheticInvestment: HomeSection = {
        id: 'synthetic-investment-philosophy',
        section_key: 'investment-philosophy',
        sort_order: 2,
        is_active: true,
        created_at: now,
        updated_at: now,
      };
      const insertAt = base.length >= 2 ? 2 : base.length;
      const nextBase: HomeSection[] = [...base.slice(0, insertAt), syntheticInvestment, ...base.slice(insertAt)];
      return nextBase;
    }

    return base;
  }, [sections]);

  // Auto-backup effect (moved out of useMemo to prevent infinite loops)
  React.useEffect(() => {
    if (effectiveSections.length > 0) {
      autoBackup(effectiveSections);
    }
  }, [effectiveSections, autoBackup]);

  // Define section themes by strict alternation based on index
  // Allow per-section theme override with safe fallback to existing parity logic
  const resolveSectionTheme = (_section: HomeSection, idx: number): 'dark' | 'light' => {
    // Override: make the second section (index 1) dark to invert header/logo as requested
    if (idx === 1) return 'dark';
    // Fallback to existing deterministic alternation
    return idx % 2 === 0 ? 'dark' : 'light';
  };

  const sectionThemes: Record<number, 'dark' | 'light'> = effectiveSections.reduce((acc, s, idx) => {
    acc[idx] = resolveSectionTheme(s, idx);
    return acc;
  }, {} as Record<number, 'dark' | 'light'>);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-3 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
          <p className="text-sm text-muted-foreground font-arabic-body">جاري تحميل المحتوى...</p>
        </div>
      </div>
    );
  }

  // Fixed hero video URL
  const heroVideoUrl = '/op-uploads/videos/2025/10/video/1761066148405-6a7492e4-81e5-4c3f-a66b-047eb2e19459.mp4';

  // Build a sectionConfig for dots from dynamic sections (Arabic names, alternated themes)
  const sectionConfig = effectiveSections.map((s, idx) => ({
    name_ar: s.title_ar || `قسم ${idx + 1}`,
    name_en: s.title_en || `Section ${idx + 1}`,
    theme: sectionThemes[idx],
  }));

  return (
    <HomeLayout hideFooter>
      <MetaTags />
      <UnifiedHeader 
        variant="home" 
        isTransparent={true} 
        currentSection={currentSection}
        sectionThemes={sectionThemes}
      />
      
	<FullpageScroll onSectionChange={updateCurrentSection} sectionConfig={sectionConfig}>
        {/* Render dynamic sections with enhanced transitions */}
        {effectiveSections.map((section, idx) => (
          <SectionTransition
            key={section.id}
            sectionIndex={idx}
            sectionKey={section.section_key}
            theme={sectionThemes[idx]}
          >
            <DynamicHomeSection
              section={section}
              backgroundImage={
                // For contact-like sections, prefer media; otherwise force the provided URL; ignore DB background.
                (section.section_key === 'contact' || section.section_key === 'contact_cta' || section.section_key === 'contact-cta')
                  ? (getSectionBackground(section.section_key) 
                    || '/op-uploads/2025/09/image/1758042473782-67b2171e-bb6b-4065-aa00-8344a6783337.webp')
                  : (getSectionBackground(section.section_key) || section.background_url || undefined)
              }
              videoUrl={section.section_key === 'hero' ? heroVideoUrl : undefined}
              fallbackImage={
                section.section_key === 'hero'
                  ? '/op-uploads/102604dd-a9aa-47f8-a7cc-9bd856994a25.png'
                  : section.section_key === 'about' || section.section_key === 'about-sectors'
                  ? '/op-uploads/DSC_5259.webp'
                  : section.section_key === 'vision'
                  ? '/op-uploads/6c14e3c1-49c9-4615-ba5b-e7d6012d332e.png'
                  : (section.section_key === 'contact' || section.section_key === 'contact_cta' || section.section_key === 'contact-cta')
                  ? '/op-uploads/2025/09/image/1758042473782-67b2171e-bb6b-4065-aa00-8344a6783337.webp'
                  : '/op-uploads/default-internal-bg.png'
              }
              sectionIndex={idx}
              overrideTheme={idx === 1 ? 'dark' : sectionThemes[idx]}
            />
          </SectionTransition>
        ))}
      </FullpageScroll>
    </HomeLayout>
  );
};

const Index = () => {
  // Use the global LanguageProvider from App.tsx to avoid state divergence
  return <IndexContent />;
};

export default Index;